import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/States";
import { Users, Search, Download, MessageSquarePlus, CalendarClock, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "qualified", "site_visit", "won", "lost"];
const S_COLOR = { new: "bg-blue-50 text-blue-600", contacted: "bg-amber-50 text-amber-warm", qualified: "bg-violet-50 text-violet-600", site_visit: "bg-cyan-50 text-cyan-600", won: "bg-brand-soft text-brand", lost: "bg-red-50 text-red-500" };

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLeads() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const { data } = useQuery({ queryKey: ["admin-leads", q, status], queryFn: () => api.get("/admin/leads", { params: { q, status } }).then((r) => r.data), refetchInterval: 15000 });

  const patch = async (id, payload) => {
    try { await api.patch(`/admin/leads/${id}`, payload); qc.invalidateQueries({ queryKey: ["admin-leads"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportCsv = () => {
    const token = localStorage.getItem("gf_token");
    fetch(`${API_URL}/api/admin/leads/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = "grofkart-leads.csv"; a.click(); URL.revokeObjectURL(url);
      }).catch(() => toast.error("Export failed"));
  };

  if (!data) return <Spinner />;
  const counts = STATUSES.reduce((m, s) => ({ ...m, [s]: data.filter((l) => l.status === s).length }), {});

  return (
    <div className="space-y-4" data-testid="admin-leads-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, city, Lead ID…" data-testid="leads-search" className="w-full bg-transparent text-sm outline-none" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leads-status-filter" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")} ({counts[s] || 0})</option>)}
        </select>
        <button onClick={exportCsv} data-testid="leads-export-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> Export</button>
      </div>

      {data.length === 0 ? (
        <EmptyState icon={Users} title="No leads yet" subtitle="Franchise enquiries from the Grofkart Store banner will appear here." />
      ) : (
        <div className="space-y-2">
          {data.map((l) => (
            <div key={l.id} className="rounded-2xl border border-slate-200 bg-white" data-testid={`lead-row-${l.id}`}>
              <button onClick={() => setOpen(open === l.id ? null : l.id)} className="flex w-full items-center gap-3 p-4 text-left">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800">{l.full_name} <span className="text-xs font-normal text-slate-400">· {l.lead_id}</span></p>
                  <p className="text-xs text-slate-500">{l.mobile} · {l.city}, {l.state} · {(l.created_at || "").slice(0, 10)}</p>
                </div>
                <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{l.source}</span>
                <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold capitalize ${S_COLOR[l.status] || "bg-slate-100"}`}>{(l.status || "new").replace("_", " ")}</span>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open === l.id ? "rotate-180" : ""}`} />
              </button>
              {open === l.id && <LeadDetail lead={l} patch={patch} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LeadDetail({ lead, patch }) {
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState(lead.assigned_to || "");
  const [fdate, setFdate] = useState("");

  const fields = [
    ["WhatsApp", lead.whatsapp], ["Email", lead.email], ["District", lead.district],
    ["Preferred location", lead.preferred_location], ["Existing business", lead.existing_business],
    ["Investment budget", lead.investment_budget], ["Commercial space", lead.commercial_space],
    ["Timeline", lead.timeline],
  ];

  return (
    <div className="border-t border-slate-100 p-4 text-sm">
      <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {fields.map(([k, v]) => v ? <div key={k} className="flex justify-between gap-2"><span className="text-slate-400">{k}</span><span className="font-medium text-slate-700">{v}</span></div> : null)}
      </div>
      {lead.message && <p className="mt-2 rounded-xl bg-slate-50 p-3 text-slate-600">“{lead.message}”</p>}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <label className="text-xs"><span className="mb-1 block font-semibold text-slate-500">Status</span>
          <select value={lead.status} onChange={(e) => patch(lead.id, { status: e.target.value })} data-testid={`lead-status-${lead.id}`} className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm capitalize outline-none focus:border-brand">
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </label>
        <label className="text-xs"><span className="mb-1 block font-semibold text-slate-500">Assign to</span>
          <div className="flex gap-1">
            <input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="Name/email" className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-brand" />
            <button onClick={() => patch(lead.id, { assigned_to: assignee })} data-testid={`lead-assign-${lead.id}`} className="rounded-lg bg-brand px-2 text-xs font-bold text-white">Set</button>
          </div>
        </label>
        <label className="text-xs"><span className="mb-1 block font-semibold text-slate-500">Schedule follow-up</span>
          <div className="flex gap-1">
            <input type="date" value={fdate} onChange={(e) => setFdate(e.target.value)} className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-brand" />
            <button onClick={() => { if (fdate) { patch(lead.id, { followup: fdate }); setFdate(""); } }} data-testid={`lead-followup-${lead.id}`} className="rounded-lg bg-brand px-2 text-xs font-bold text-white"><CalendarClock className="h-3.5 w-3.5" /></button>
          </div>
        </label>
      </div>

      <div className="mt-3 flex gap-1">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid={`lead-note-input-${lead.id}`} className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-brand" />
        <button onClick={() => { if (note.trim()) { patch(lead.id, { note }); setNote(""); } }} data-testid={`lead-note-btn-${lead.id}`} className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-3 text-xs font-bold text-white"><MessageSquarePlus className="h-3.5 w-3.5" /> Note</button>
      </div>

      {(lead.notes?.length > 0 || lead.followups?.length > 0 || lead.history?.length > 0) && (
        <div className="mt-3 space-y-1 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
          {(lead.followups || []).map((f, i) => <p key={`f${i}`}>📅 Follow-up: {f}</p>)}
          {(lead.notes || []).map((n, i) => <p key={`n${i}`}>📝 {n.text || n} <span className="text-slate-400">{n.at ? `· ${n.at.slice(0, 16).replace("T", " ")}` : ""}</span></p>)}
          {(lead.history || []).map((h, i) => <p key={`h${i}`} className="text-slate-400">• {h.action} · {(h.at || "").slice(0, 16).replace("T", " ")}</p>)}
        </div>
      )}
    </div>
  );
}
